import { Disclosure, Menu, Transition } from '@headlessui/react';
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { classNames } from '../../../utils/helpers/helpers';
import TokenService from '../../../services/token.service';
import Select from '../../forms/Select/Select';
import { ExamYearContext } from '../../../utils/contexts/ExamYearContext';
import { findCurrentItem, resolveNavigation } from './navigation';
import { MobileNavLink, NavDropdown, NavTab } from './NavItems';

const userNavigation = [
  // { name: 'Your Profile', href: '#' },
  // { name: 'Settings', href: '#' },
  { name: 'Sign out', href: '#' },
];

export default function NavBar({ user, setUser }) {
  const [t] = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { yearList, examYear, updateExamYear } = useContext(ExamYearContext);

  const [currentPage, setCurrentPage] = useState('');
  const [years, setYears] = useState([]);

  // Дерево навигации целиком описано в ./navigation.js — каждый пункт
  // объявлен там ровно один раз и сам знает свою секцию и свои ветки.
  const navTree = useMemo(
    () => resolveNavigation(user, t, location?.pathname),
    [user, t, location?.pathname],
  );

  useEffect(() => {
    const item = findCurrentItem(navTree, location?.pathname);
    if (item) setCurrentPage(item.name);
    // else-ветки НЕТ намеренно: на роутах вне меню (/admin/results/newupload,
    // /7315555e-.../results) заголовок остаётся прежним — ровно как раньше.
    // /forms и /forms/:uuid/edit теперь в меню («Формы» у SSA и TRADMIN).
    // Пустой <h1> дал бы белую полосу в py-6.

    return () => {};
  }, [location, navTree]);

  useEffect(() => {
    if (yearList?.length) {
      var options = yearList?.map((y) => ({
        label: y?.year,
        value: y?.uuid,
        data: y,
      }));
      setYears(options);
    }
  }, [updateExamYear, yearList]);

  return (
    <>
      <div className='min-h-full w-screen bg-sheet'>
        <Disclosure as='nav' className='bg-gray-800'>
          {({ open, close }) => (
            <>
              <div className='mx-auto px-4 sm:px-6 lg:px-8'>
                <div className='flex min-h-[4rem] items-center justify-between py-2'>
                  <div className='flex items-center'>
                    {/* <div className='flex-shrink-0'> */}
                    {/* <img src={logo} alt='logo' width='32' height='32' /> */}
                    {/* </div> */}
                    <div className='hidden lg:block'>
                      {/* Корневой <Disclosure as='nav'> — уже безымянный landmark,
                          поэтому внутреннему обязательно нужна своя подпись.
                          overflow-x-visible обязателен: overflow-x-auto обрежет выпадашки. */}
                      <nav
                        aria-label='Разделы'
                        className='flex items-stretch space-x-1 overflow-x-visible'
                      >
                        {navTree.map((node) =>
                          node.type === 'group' ? (
                            <NavDropdown
                              key={node.id}
                              group={node}
                              pathname={location?.pathname}
                            />
                          ) : (
                            <NavTab
                              key={node.id}
                              item={node}
                              pathname={location?.pathname}
                            />
                          ),
                        )}
                      </nav>
                    </div>
                  </div>
                  <div className='hidden min-w-fit lg:block'>
                    <div className='ml-4 flex items-center lg:ml-6'>
                      {/* <button
                        type="button"
                        className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                      >
                        <span className="sr-only">View notifications</span>
                        <BellIcon className="h-6 w-6" aria-hidden="true" />
                      </button> */}

                      {/* Profile dropdown */}
                      <Select
                        customStyles={null}
                        isSearchable={false}
                        options={years}
                        value={examYear?.uuid}
                        onChange={(e) => {
                          updateExamYear(e.data);
                        }}
                      />
                      <Menu as='div' className='relative ml-3'>
                        <div>
                          <Menu.Button className='rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800'>
                            <span className='sr-only'>Open user menu</span>
                            <UserCircleIcon
                              className='h-6 w-6'
                              aria-hidden='true'
                            />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          enter='transition ease-out duration-100'
                          enterFrom='transform opacity-0 scale-95'
                          enterTo='transform opacity-100 scale-100'
                          leave='transition ease-in duration-75'
                          leaveFrom='transform opacity-100 scale-100'
                          leaveTo='transform opacity-0 scale-95'
                        >
                          <Menu.Items className='ring-black absolute right-0 z-30 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-opacity-5 focus:outline-none'>
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => (
                                  <button
                                    // href={item.href}
                                    className={classNames(
                                      active ? 'bg-gray-100' : '',
                                      'block w-full px-4 py-2 text-left text-sm text-gray-700',
                                    )}
                                    onClick={() => {
                                      TokenService.removeTokens();
                                      setUser(null);
                                      navigate('/login');
                                    }}
                                  >
                                    {item.name}
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                  <div className='-mr-2 flex lg:hidden'>
                    {/* Mobile menu button */}
                    <Disclosure.Button className='inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800'>
                      <span className='sr-only'>Open main menu</span>
                      {open ? (
                        <XMarkIcon
                          className='block h-6 w-6'
                          aria-hidden='true'
                        />
                      ) : (
                        <Bars3Icon
                          className='block h-6 w-6'
                          aria-hidden='true'
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className='lg:hidden'>
                {/* Вложенных выпадашек нет: панель и так вертикальная, группы —
                    просто подписанные секции, каждая страница в один тап. */}
                <div className='max-h-[70vh] space-y-1 overflow-y-auto px-2 pt-2 pb-3 sm:px-3'>
                  {navTree.map((node) =>
                    node.type === 'group' ? (
                      <div
                        key={node.id}
                        role='group'
                        aria-labelledby={`nav-group-${node.id}`}
                        className='pt-2 first:pt-0'
                      >
                        <div
                          id={`nav-group-${node.id}`}
                          className='px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400'
                        >
                          {node.name}
                        </div>
                        <div className='space-y-1'>
                          {node.children.map((child) => (
                            <MobileNavLink
                              key={child.id}
                              item={child}
                              pathname={location?.pathname}
                              onNavigate={close}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <MobileNavLink
                        key={node.id}
                        item={node}
                        pathname={location?.pathname}
                        onNavigate={close}
                      />
                    ),
                  )}
                </div>
                {/* Ряд навигации живёт на lg:, поэтому 768-1023px теперь видят эту
                    панель — а селектор года был только в desktop-блоке. */}
                <div className='border-t border-gray-700 px-4 py-3'>
                  <Select
                    customStyles={null}
                    isSearchable={false}
                    options={years}
                    value={examYear?.uuid}
                    onChange={(e) => {
                      updateExamYear(e.data);
                    }}
                  />
                </div>
                <div className='border-t border-gray-700 pt-4 pb-3'>
                  <div className='flex items-center px-5'>
                    <div className='flex-shrink-0'>
                      {/* <img
                        className='h-10 w-10 rounded-full'
                        src={user.imageUrl}
                        alt=''
                      /> */}
                    </div>
                    <div className='ml-3'>
                      <div className='text-base font-medium leading-none text-white'>
                        {user?.name}
                      </div>
                      <div className='text-sm font-medium leading-none text-gray-400'>
                        {user?.name}
                      </div>
                    </div>
                    <button
                      type='button'
                      className='ml-auto flex-shrink-0 rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800'
                    >
                      <span className='sr-only'>View notifications</span>
                      <BellIcon className='h-6 w-6' aria-hidden='true' />
                    </button>
                  </div>
                  <div className='mt-3 space-y-1 px-2'>
                    {userNavigation.map((item) => (
                      <Disclosure.Button
                        key={item.name}
                        className='block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white'
                        onClick={() => {
                          TokenService.removeTokens();
                          setUser(null);
                          navigate('/login');
                        }}
                      >
                        {item.name}
                      </Disclosure.Button>
                    ))}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <header className='bg-white shadow'>
          <div className='mx-auto py-6 px-4 sm:px-6 lg:px-8'>
            <h1 className='text-3xl font-bold tracking-tight text-gray-900'>
              {currentPage}
            </h1>
          </div>
        </header>

        <main>
          <div className='mx-auto py-6 sm:px-6 lg:px-8'>
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
